import { Injectable, OnDestroy, Signal, resource } from '@angular/core';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import {
  GroupChartTabs,
  GroupedTabData,
} from '@services/analytics-services/core/group-chart.modals';
import {
  GroupedDataState,
  GroupedDataStoreService,
} from '@services/analytics-services/core/grouped-data-store.service';
import { MatrixStatService } from '@services/analytics-services/core/matrix-stat.service';
import {
  ChartType,
  EventStats,
  QueryType,
  ViewStats,
  VisitorStats,
} from '@services/analytics-services/core/models/matrix-stats';
import { DEVICE_COLORS } from '@services/analytics-services/hight-chart-configurations';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { InsightsAnalyticsService } from '@services/analytics-services/insights/insights-analytics.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { DateRangePresetType, Pagination, ProcessingState } from '@services/core-services/models';
import { Observable, Subject, catchError, of, tap, firstValueFrom } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';

@Injectable()
export class DashboardInsightsStore implements OnDestroy {
  private visitorsBySourceState: GroupedDataState;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly insightsAnalyticsService: InsightsAnalyticsService,
    private readonly matrixStatService: MatrixStatService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly groupedDataStoreService: GroupedDataStoreService
  ) {
    this.visitorsBySourceState = this.groupedDataStoreService.createGroupedDataState('source');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Resource Factory Methods ---

  /**
   * Creates a resource for loading active users data
   */
  public createActiveUsersResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<number> => {
        const response = await firstValueFrom(this.insightsAnalyticsService.getActiveUsers(params));
        return response?.active_users ?? 0;
      },
    });
  }

  /**
   * Creates a resource for loading visitor analytics data
   */
  public createVisitorAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<AnalyticsInfo<VisitorStats>> => {
        const { range, compare, interval, preset } = params;
        const analytics = await firstValueFrom(
          this.matrixStatService.getMatrixAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
            query_type: QueryType.METRICS,
            chart_type: ChartType.VISITOR_STATS,
            timezone: this.dynamicQueryService.getUserTimezone(),
          })
        );
        return analytics as AnalyticsInfo<VisitorStats>;
      },
    });
  }

  /**
   * Creates a resource for loading view analytics data
   */
  public createViewAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<AnalyticsInfo<ViewStats>> => {
        const { range, compare, interval, preset } = params;
        const analytics = await firstValueFrom(
          this.matrixStatService.getMatrixAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
            query_type: QueryType.METRICS,
            chart_type: ChartType.PAGE_VIEW_STATS,
            timezone: this.dynamicQueryService.getUserTimezone(),
          })
        );
        return analytics as AnalyticsInfo<ViewStats>;
      },
    });
  }

  /**
   * Creates a resource for loading event analytics data
   */
  public createEventAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<AnalyticsInfo<EventStats>> => {
        const { range, compare, interval, preset } = params;
        const analytics = await firstValueFrom(
          this.matrixStatService.getMatrixAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
            query_type: QueryType.METRICS,
            chart_type: ChartType.EVENT_STATS,
            timezone: this.dynamicQueryService.getUserTimezone(),
          })
        );
        return analytics as AnalyticsInfo<EventStats>;
      },
    });
  }

  /**
   * Creates a resource for loading traffic matrix data
   */
  public createTrafficMatrixResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<any[]> => {
        const response = await firstValueFrom(
          this.insightsAnalyticsService.getWebsiteTraffic(params)
        );
        return response?.list || [];
      },
    });
  }

  /**
   * Creates a resource for loading page stats chart data
   */
  public createPageStatsChartResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const data: { event_name: string; events_count: number }[] = await firstValueFrom(
          this.insightsAnalyticsService.getPageStat(params)
        );

        if (!data || data.length === 0) {
          return undefined;
        }

        const pageStats = data.map(stat => ({
          label: stat.event_name,
          value: stat.events_count,
        }));

        return {
          categories: pageStats.map(stat => stat.label ?? 'null'),
          data: pageStats.map(stat => ({
            y: stat.value,
            label: stat.label,
          })),
          yAxisTitle: 'Number of Pages',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.options.label}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                    <div class="d-flex align-items-center spacing-4 w-100 text-center">
                      <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                      <div class="h4-reg primary-50">Page Visits</div>
                    </div>
                    <div class="h4-bold primary-0">${point.options.y}</div>
                </div>
              </div>
            </div>
          `,
        };
      },
    });
  }

  /**
   * Creates a resource for loading visitors by device chart data
   */
  public createVisitorsByDeviceChartResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const response = await firstValueFrom(
          this.insightsAnalyticsService.getVisitorsByDevice(params)
        );

        const list = response?.list || [];
        if (list.length === 0) {
          return undefined;
        }

        const deviceData = list.map((item: { device: string; unique_users: number }) => {
          const deviceType = (item.device || 'unknown').toLowerCase();
          return {
            label: item.device || 'Unknown',
            y: item.unique_users,
            color: DEVICE_COLORS[deviceType] || undefined,
          };
        });

        const totalVisitors = deviceData.reduce((sum: any, item: any) => sum + item.y, 0);
        deviceData.sort((a: { y: number }, b: { y: number }) => b.y - a.y);

        return {
          data: deviceData,
          donut: true,
          title: 'Device Distribution',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.name}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                    <div class="h4-reg primary-50">Unique Visitors</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y} (${point.percentage?.toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          `,
          donutOptions: {
            centerText: {
              text: `${totalVisitors.toLocaleString()}`,
              subtext: 'Total Visitors',
            },
          },
        };
      },
    });
  }

  /**
   * Creates a resource for loading server events chart data
   */
  public createServerEventsChartResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const list: any = await firstValueFrom(
          this.insightsAnalyticsService.getServerEventsStat(params)
        );

        if (!list || list.length === 0) {
          return undefined;
        }

        const serveEventeData = list.map((item: { event_name: string; events_count: number }) => {
          const deviceType = (item.event_name || 'unknown').toLowerCase();
          return {
            label: item.event_name || 'Unknown',
            y: item.events_count,
            color: DEVICE_COLORS[deviceType] || undefined,
          };
        });

        const totalEvents = serveEventeData.reduce(
          (sum: number, item: { y: number }) => sum + item.y,
          0
        );

        return {
          data: serveEventeData,
          donut: true,
          title: 'Popular Events',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0" style="margin: 0">${point.name}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"></div>
                    <div class="h4-reg primary-50">Event Counts</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y} (${point.percentage?.toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          `,
          donutOptions: {
            centerText: {
              text: `${totalEvents.toLocaleString()}`,
              subtext: 'Total Events',
            },
          },
        };
      },
    });
  }

  // --- Visitors by Source (Complex grouped data - keeping existing pattern for now) ---

  getVisitorsBySourceStats = (): Observable<GroupedTabData> =>
    this.visitorsBySourceState.stats.asObservable();

  getVisitorsBySourceTabs = (): Observable<GroupChartTabs[]> =>
    this.visitorsBySourceState.tabs.asObservable();

  getVisitorsBySourceActiveTab = (): Observable<string> =>
    this.visitorsBySourceState.defaultTab.asObservable();

  getVisitorsBySourcePagination = (): Observable<Pagination> =>
    this.visitorsBySourceState.pagination.asObservable() as Observable<Pagination>;

  getVisitorsBySourceLoader = (): Observable<ProcessingState> =>
    this.visitorsBySourceState.loader.asObservable();

  loadVisitorsBySourceData(context: AnalyticsRangeContext): Observable<any> {
    this.visitorsBySourceState.loader.next(ProcessingState.Loading);
    return this.insightsAnalyticsService.getVisitors(context).pipe(
      tap(response => {
        if (!response || response.list.length === 0) {
          this.visitorsBySourceState.loader.next(ProcessingState.Empty);
          return;
        }
        const data = this.extractResponseList(response);
        this.groupedDataStoreService.processEntityGroupedData(
          this.visitorsBySourceState,
          data,
          undefined,
          'visitorsBySource'
        );
        this.visitorsBySourceState.loader.next(ProcessingState.Success);
      }),
      catchError(err => {
        console.error(`Error loading visitors by source data:`, err);
        this.visitorsBySourceState.loader.next(ProcessingState.Error);
        return of({ list: [] });
      })
    );
  }

  updateVisitorsBySourceDefaultTab(tabId: string): void {
    this.groupedDataStoreService.updatePagination(this.visitorsBySourceState, tabId, 1);
    this.visitorsBySourceState.defaultTab.next(tabId);
  }

  updateVisitorsBySourcePagination(tabId: string, page: number, limit: number = 8): void {
    this.groupedDataStoreService.updatePagination(this.visitorsBySourceState, tabId, page, limit);
  }

  private extractResponseList(response: any): any[] {
    if (response && response.content && Array.isArray(response.content.list)) {
      return response.content.list;
    } else if (response && Array.isArray(response.list)) {
      return response.list;
    } else if (response && Array.isArray(response)) {
      return response;
    }
    console.warn('Could not extract list from response:', response);
    return [];
  }
}
